import sortBy from 'lodash/sortBy'

import { BlockType } from 'common/builder/constants/BlockType'
import { NodeType } from 'common/builder/constants/NodeType'
import { sortBlocks } from 'common/builder/nodes/split/splitNodeHelpers'

/**
 * @typedef {Object} Target
 * @property {string} id
 * @property {string} type
 * @property {string} src
 * @property {string} srcContent
 * @property {boolean} [invalid=false]
 */
/**
 * Adds target to the targets list. Mutates `list` arg
 * @param {Array} list
 * @param {Target} target
 * @param {Object} contents
 * @param {Object} actionGroups
 */
const pushTarget = (list, target, { nodes }) => {
  if (Number.isFinite(target.id)) {
    const c = Object.values(nodes).find((c) => c.contentId === target.id)
    if (c != null) {
      target.id = c.id
    } else {
      target.invalid = true
    }
  }
  if (nodes[target.id] && nodes[target.id].nodeType) {
    target.type = nodes[target.id].nodeType
  }

  list.push(target)
}

/**
 * Returns list of targets where node refers to
 * @param {Object} el Node from where connection starts. May be Content or ActionGroup
 * @param {Object} data
 * @param {string} contentId
 * @returns {Array.<Target>}
 */
export const getNodeTargets = (el, data, contentId) => {
  const { blocks, buttons } = data
  let res = []

  if (el.blocks) {
    let blockList = el.blocks

    if (el.type === BlockType.CARDS) {
      blockList = el.blocks.slice().reverse()
    } else if (el.nodeType === NodeType.SPLIT) {
      const items = el.blocks.map((id) => blocks[id] || {})
      blockList = sortBlocks(items).map((b) => b.id)
    } else if (el.nodeType === NodeType.CONDITION) {
      const items = el.blocks.map((id) => blocks[id] || {})
      blockList = sortBy(items, (i) => (i.type === BlockType.CASE ? 0 : 1)).map((b) => b.id)
    }

    for (let blockId of blockList) {
      const block = blocks[blockId] || {}

      if (block.targetId) {
        pushTarget(res, { id: block.targetId, src: block.id, srcContent: contentId || el.id }, data)
      }

      if (block.buttons) {
        for (let btnId of block.buttons) {
          const btn = buttons[btnId]
          if (btn && btn.targetId) {
            pushTarget(res, { id: btn.targetId, src: btn.id, srcContent: contentId || el.id }, data)
          }
        }
      }
      if (block.blocks.length > 0) {
        res = [...res, ...getNodeTargets(block, data, el.id)]
      }
    }
  }

  if (el.targetId) {
    pushTarget(res, { id: el.targetId, src: el.id }, data)
  }

  return res
}

const _mouseDownPosition = { x: 0, y: 0 }
let _clickDistance = 0

// Using pointer event if possible, as PIXI does, cause if we will use only mouse event,
// preventDefault on pointer event will stop mouse events from firing
const eventSource = window.PointerEvent ? 'pointer' : 'mouse'

window.addEventListener(
  `${eventSource}down`,
  (e) => {
    _clickDistance = 0
    _mouseDownPosition.x = e.clientX
    _mouseDownPosition.y = e.clientY
  },
  true,
)

window.addEventListener(
  `${eventSource}up`,
  (e) => {
    _clickDistance = Math.max(
      Math.abs(e.clientX - _mouseDownPosition.x),
      Math.abs(e.clientY - _mouseDownPosition.y),
    )
  },
  true,
)

export const getUserPlatform = () => {
  const platform = window.navigator.platform.toLowerCase()
  if (platform.includes('mac')) {
    return 'mac'
  }
  if (platform.includes('win')) {
    return 'win'
  }

  return 'other'
}

export const getClickDistance = () => _clickDistance

export const manageFixedArray = (array, newItem, limit) => {
  if (array.length >= limit) {
    array.shift() // Remove the first item
  }
  array.push(newItem) // Add the new item to the end of the array
  return array
}
